import { FC, useEffect, useId, useRef } from 'react'

import { GRAY_700 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import Modal from 'components/common/Modals/Modal/Modal'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

interface Props {
  open: boolean
  title: string
  body: string
  confirmLabel: string
  cancelLabel: string
  /** Red confirm, for an action that cannot be taken back */
  destructive?: boolean
  /** Over another dialog (the post editor's removal): paints above it */
  zIndex?: number
  onConfirm: () => void
  onCancel: () => void
}

/**
 * A confirmation (prototype v3 `confirm`): a small accessible alert dialog, a sheet on a phone with its
 * actions stacked (the primary on top), the question as its title, what happens as its body, the safe
 * choice focused. Escape and a click on the scrim cancel; there is no close button. Confirms at most once per
 * opening: the `Modal` keeps a closing dialog on screen, clickable, for its exit transition.
 */
export const CampaignConfirmDialog: FC<Props> = ({ open, title, body, confirmLabel, cancelLabel, destructive = false, zIndex, onConfirm, onCancel }) => {
  const bodyId = `${useId().replace(/:/g, '')}-body`

  // Reset when it opens, not when it unmounts: a reopening during the exit reuses the closing dialog
  const confirmedRef = useRef(false)
  useEffect(() => {
    if (open) confirmedRef.current = false
  }, [open])

  const confirm = () => {
    if (confirmedRef.current) return
    confirmedRef.current = true
    onConfirm()
  }

  return (
    <Modal
      isOpen={open}
      isAccessibleDialog
      dialogRole="alertdialog"
      ariaDescribedBy={bodyId}
      size="sm"
      isSheetOnPhone
      zIndex={zIndex}
      title={title}
      onClickOutside={onCancel}
      footerContent={
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" gap={1}>

          <MUIButton type="secondaryNoBorder" data-autofocus onClick={onCancel}>{cancelLabel}</MUIButton>

          <MUIButton type={destructive ? 'orangePrimary' : 'defaultPrimary'} onClick={confirm}>{confirmLabel}</MUIButton>

        </Stack>
      }
    >
      <Typography id={bodyId} variant="text-sm" component="p" color={GRAY_700}>{body}</Typography>
    </Modal>
  )
}
